import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articleAPI } from '../services/api';
import { trackArticleView, trackEvent } from '../Utils/analytics';
import AdUnit from '../components/AdUnit';
import { CoinbaseBanner } from '../components/CoinbaseAffiliate';
import './ArticleDetailPage.css';
import RelatedArticles from '../components/RelatedArticles';

function ArticleDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await articleAPI.getArticleById(id);
        setArticle(response.data);

        // ✅ Track article view with Google Analytics
        if (response.data) {
          trackArticleView(response.data);
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      crypto: '#f39c12',
      ai: '#3498db',
      politics: '#e74c3c'
    };
    return colors[category] || '#666';
  };

  const getSentimentEmoji = (sentiment) => {
    const emojis = {
      positive: '📈',
      negative: '📉',
      neutral: '➡️'
    };
    return emojis[sentiment] || '➡️';
  };

  // Track when user clicks on a topic tag
  const handleTopicClick = (topic) => {
    trackEvent('topic_click', {
      topic: topic,
      article_id: article.id,
      category: article.category
    });
  };



  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="error-container">
        <h2>Article not found</h2>
        <p>{error || 'Article not found'}</p>
        <Link to="/" className="back-button">← Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="article-detail-cnn">
      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to={`/${article.category}`}>{article.category}</Link>
          <span className="breadcrumb-separator">/</span>
          <span>Article</span>
        </div>
      </div>

      <div className="article-container">
        <div className="article-grid">
          {/* Main Article Content */}
          <article className="article-main">
            {/* Category Badge */}
            <span
              className="category-badge-large"
              style={{ backgroundColor: getCategoryColor(article.category) }}
            >
              {article.category?.toUpperCase()}
            </span>

            {/* Breaking Badge */}
            {article.is_breaking && (
              <span className="breaking-badge">
                🔥 BREAKING NEWS
              </span>
            )}

            {/* Article Title */}
            <h1 className="article-title">{article.title}</h1>

            {/* Article Meta */}
            <div className="article-meta-detail">
              <div className="meta-left">
                {article.url ? (
                  <a href={article.url} target="_blank" rel="noopener noreferrer" className="article-source-detail article-source-link">
                    {article.source_name?.toLowerCase().includes('focusview') ? article.source_name : `${article.source_name} via FocusView.ai`} ↗
                  </a>
                ) : (
                  <span className="article-source-detail">{article.source_name?.toLowerCase().includes('focusview') ? article.source_name : `${article.source_name} via FocusView.ai`}</span>
                )}
                <span className="meta-separator">•</span>
                <span className="article-date">{formatDate(article.published_at)}</span>
              </div>
              <div className="meta-right">
                {article.sentiment && (
                  <span className="sentiment-badge">
                    {getSentimentEmoji(article.sentiment)} {article.sentiment}
                  </span>
                )}
                <span className="view-count">👁️ {article.view_count || 0} views</span>
              </div>
            </div>

            {/* Featured Image */}
            {article.image_url && (
              <img
                src={article.image_url}
                alt={article.title}
                className="article-image-detail"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}

            {/* Ad space hidden - uncomment when adding Google AdSense */}
            <AdUnit slot="5555555555" style={{ minHeight: '90px', margin: '16px 0' }} />

            {/* AI Summary Box */}
            {article.ai_summary && (
              <div className="summary-box">
                <div className="summary-label">AI SUMMARY</div>
                <p className="summary-text">{article.ai_summary}</p>
              </div>
            )}

            {/* Article Content */}
            <div className="article-content-detail">
              {article.summary && (
                <div className="article-summary">
                  <strong>Summary:</strong> {article.summary}
                </div>
              )}
              {article.content && <p>{article.content}</p>}
            </div>

            {/* Ad space hidden - uncomment when adding Google AdSense */}
            <CoinbaseBanner style={{ margin: '20px 0' }} />

            {/* Topics/Tags */}
            {article.topics && (
              <div className="article-topics">
                <strong>Topics:</strong>
                {article.topics.split(',').map((topic, idx) => (
                  <Link
                    key={idx}
                    to={`/search?q=${encodeURIComponent(topic.trim())}`}
                    className="topic-tag"
                    onClick={() => handleTopicClick(topic.trim())}
                  >
                    {topic.trim()}
                  </Link>
                ))}
              </div>
            )}

            {/* Source link removed — Grok AI articles via focusview.ai */}

            {/* Ad space hidden - uncomment when adding Google AdSense */}
            <AdUnit slot="6666666666" style={{ minHeight: '250px', margin: '16px 0' }} />

            {/* Related Articles Section */}
            <div className="related-section">
              <h2 className="related-title">Related Stories</h2>
              <RelatedArticles articleId={id} />
            </div>
          </article>

          {/* Sidebar */}
          <aside className="article-sidebar">
            {/* Ad space hidden - uncomment when adding Google AdSense */}
            {/* <div className="sidebar-sticky">
              <AdUnit slot="7777777777" style={{ minHeight: '600px', position: 'sticky', top: '20px' }} />
            </div> */}
          </aside>
        </div>
      </div>
    </div>
  );
}

export default ArticleDetailPage;